import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, catchError, map, of } from 'rxjs';
import {
  SupportReceivedResult,
  SupportReceivedResultGrouped
} from './stromstotte.types';
import { ConsumptionService } from '../consumption/consumption.service';
import { isBefore } from 'date-fns';
import { theTime } from 'src/app/utils/date-utils';

@Injectable({
  providedIn: 'root'
})
export class StromstotteService {
  stotteDictionary = new Map<string, SupportReceivedResult>();

  private _stromstotteData$ = new ReplaySubject<SupportReceivedResult>(
    undefined
  );
  private _stromstotteDataGrouped$ =
    new ReplaySubject<SupportReceivedResultGrouped>(undefined);
  private _stromstotteDataForDay$ = new ReplaySubject<SupportReceivedResult>(
    undefined
  );

  stromstotteResult$: Observable<SupportReceivedResult | undefined> =
    this._stromstotteData$.asObservable();

  stromstotteResultGrouped$ = (
    objectid: string,
    date: Date
  ): Observable<SupportReceivedResultGrouped | undefined> => {
    const key = this.getDictKey(date, objectid);
    if (this.stotteDictionary.has(key)) {
      this._stromstotteData$.subscribe((res) => {
        this._stromstotteDataGrouped$.next(
          this.getStromstotteHourlyGroupedByDay(res)
        );
      });
    } else {
      this.fetchData(objectid, date);
    }
    return this._stromstotteDataGrouped$.asObservable();
  };

  stromstotteResultForDay$ = (
    objectid: string,
    day: Date
  ): Observable<SupportReceivedResult | undefined> => {
    const key = this.getDictKey(day, objectid);
    if (this.stotteDictionary.has(key)) {
      this._stromstotteData$.subscribe((res) => {
        this._stromstotteDataForDay$.next(
          this.getStromstotteHourlyForDay(res, day)
        );
      });
    } else {
      this.fetchData(objectid, day);
    }
    return this._stromstotteDataForDay$.asObservable();
  };

  constructor(private _consumptionService: ConsumptionService) { }

  fetchData(objectid: string, selectedDate: Date) {
    if (objectid && selectedDate) {
      this.getStromstotteHourly(objectid, selectedDate).subscribe((res) => {
        this._stromstotteData$.next(res);
        this._stromstotteDataGrouped$.next(
          this.getStromstotteHourlyGroupedByDay(res)
        );
        this._stromstotteDataForDay$.next(
          this.getStromstotteHourlyForDay(res, selectedDate)
        );
      });
    }
  }

  getStromstotteHourly(
    objectid: string,
    selectedDate: Date
  ): Observable<SupportReceivedResult> {
    if (isBefore(selectedDate, new Date(2023, 8, 1))) {
      return of(undefined);
    }
    const key = this.getDictKey(selectedDate, objectid);
    if (this.stotteDictionary.has(key)) {
      return of(this.stotteDictionary.get(key));
    } else {
      this.stotteDictionary.set(key, undefined);
      return this._consumptionService
        .getGovSupport(
          objectid,
          selectedDate.getFullYear(),
          selectedDate.getMonth() + 1
        )
        .pipe(
          map((supportHours) => {
            if (supportHours && supportHours.length > 0) {
              const receivesHourly = supportHours.map((supportHour) => ({
                period: supportHour.period,
                receives: supportHour.support
              }));

              const supportResult: SupportReceivedResult = {
                kunde_id: supportHours[0].kunde_id.toString(),
                fk_salgsordre: supportHours[0].fk_salgsordre.toString(),
                objectid: supportHours[0].objectid,
                period: Number(theTime(selectedDate).to(["YYYY", "MM"])),
                receivesHourly,
                receivesTotal: receivesHourly.reduce(
                  (acc, curr) => acc + curr.receives,
                  0
                )
              };
              this.stotteDictionary.set(key, supportResult);
              return supportResult;
            }
          }),
          catchError((err) => {
            return of(undefined);
          })
        );
    }
  }

  getStromstotteHourlyGroupedByDay(
    support: SupportReceivedResult
  ): SupportReceivedResultGrouped {
    var groupBy = function(xs, key) {
      return xs.reduce(function(rv, x) {
        (rv[x[key]] = rv[x[key]] || []).push(x);
        return rv;
      }, {});
    };

    if (support) {
      const groupedByDay: {
        [key: string]: {
          date: string;
          period: number;
          receives: number;
        }[];
      } = groupBy(
        support.receivesHourly.map((y) => ({
          ...y,
          date: y.period.toString().substring(0, 8)
        })),
        'date'
      );
      const totalGroupedByDay = Object.keys(groupedByDay).map((key) => {
        return {
          date: new Date(
            Number(key.substring(0, 4)),
            Number(key.substring(4, 6)) - 1,
            Number(key.substring(6, 8))
          ),
          receives: groupedByDay[key].reduce(
            (acc, curr) => acc + curr.receives,
            0
          )
        };
      });
      return {
        kunde_id: support.kunde_id,
        fk_salgsordre: support.fk_salgsordre,
        objectid: support.objectid,
        period: support.period,
        receivesHourlyGrouped: groupedByDay,
        receivesTotalByGrouped: totalGroupedByDay
      };
    }
  }

  getStromstotteHourlyForDay(
    support: SupportReceivedResult,
    date: Date
  ): SupportReceivedResult {
    if (support) {
      const receivesForDay = support.receivesHourly.filter(
        (y) =>
          y.period.toString().substring(0, 4) ===
          theTime(date).get("YYYY") &&
          y.period.toString().substring(4, 6) ===
          theTime(date).get("MM") &&
          y.period.toString().substring(6, 8) ===
          theTime(date).get("DD")
      );
      return {
        ...support,
        receivesHourly: receivesForDay,
        receivesTotal: receivesForDay.reduce(
          (acc, curr) => acc + curr.receives,
          0
        )
      };
    }
  }

  private getDictKey = (date: Date, objectid: string) =>
    `${theTime(date).to(["MM", "YYYY"], "-")}-${objectid}`
}
